import type React from 'react';

import { useI18n } from '@change-corgi/core/react/i18n';

import type { DynamicTaskShape } from '../../types';

import { TaskHeader } from './components/TaskHeader';
import { getPrimaryActionText, renderExpandedContent, renderStartBadge } from './helpers';

type DynamicTaskProps = Readonly<{
	task: DynamicTaskShape;
	isExpanded: boolean;
	onToggleExpand: () => void;
}>;

/**
 * Renders a single dynamic task with header, optional details, and action button.
 * Supports mobile-responsive layout with edge-to-edge hover highlighting.
 */
export const DynamicTask: React.FC<DynamicTaskProps> = ({ task, isExpanded, onToggleExpand }) => {
	const { translate } = useI18n();
	const { title, description, reasoning, isFirstActive, featureKey } = task;
	const primaryActionText = getPrimaryActionText(featureKey, translate);

	return (
		<div
			className="border-stroke-weaker group hover:bg-fill-inverse-weak -mx-4 border-b border-solid px-4 py-5 transition-colors duration-100 first:border-t md:-mx-12 md:px-12"
			data-qa="dynamic-task"
		>
			<div className="flex flex-col gap-3">
				{isFirstActive && renderStartBadge(translate)}
				<TaskHeader
					title={title}
					isFirstActive={isFirstActive}
					isExpanded={isExpanded}
					primaryActionText={primaryActionText}
					onToggleExpand={onToggleExpand}
				/>
				{isExpanded && renderExpandedContent(description, reasoning, isFirstActive, primaryActionText)}
			</div>
		</div>
	);
};
